/**
 * El plano de la casa — esquema versión 2 y sus adaptadores.
 *
 * Escalón E0 del plan de diseño (2026-08-22). El plano es DE LA CASA y sobrevive
 * a las visitas: recintos (la sala, el jardín, el predio) y servicios fijos (el
 * tablero, la toma de agua, el rack). Los objetos de cada rubro (aspersores,
 * cámaras, contactos) son de la cotización, no de aquí.
 *
 *   casas/{id}.plano
 *     version: 2
 *     plantas:  { pb: { id, nombre, orden } }
 *     recintos: { [id]: { id, planta, nombre, tipo: interior|exterior|predio,
 *                         poligono: [{x,y}], cerrado, actualizadoEn } }
 *     servicios: { tablero, medidorCFE, tomaAgua, acometidaInternet, rack }  → { planta, x, y }
 *
 * UNA SOLA VERDAD desde E0: el escalón 9a (2026-08-20) guardó `plano.riego` y
 * `plano.camaras` en versión 1. Aquí se normaliza al LEER (una versión 1 se ve
 * como versión 2) y se adapta al ESCRIBIR (lo que el lienzo viejo devuelve se
 * convierte en un parche de recintos/servicios). Así nunca coexisten un jardín
 * editable por el motor de riego y otro editable por el `Plano` unificado.
 *
 * Mapas por id, nunca arreglos: es lo que permite que dos dispositivos editen
 * recintos distintos sin pisarse (ver `datos/fusion.js`).
 */

import { CENTINELA_BORRAR } from '../datos/fusion.js'

export const VERSION_PLANO = 2

/** Qué recinto y qué servicio representa cada lienzo de la versión 1. */
export const RECINTO_DEL_MOTOR = {
  riego:   { id: 'jardin', nombre: 'Jardín', tipo: 'exterior', servicio: 'tomaAgua', campo: 'toma' },
  camaras: { id: 'predio', nombre: 'Predio', tipo: 'predio',   servicio: 'rack',     campo: 'rack' }
}

const PLANTA_BASE = { pb: { id: 'pb', nombre: 'Planta baja', orden: 0 } }

/** Punto tolerante: acepta `{x,y}` o `[x,y]` y devuelve siempre `{x,y}`. */
export const aPunto = p => (Array.isArray(p) ? { x: p[0], y: p[1] } : p && { x: p.x, y: p.y })

export const planoVacio = () => ({ version: VERSION_PLANO, plantas: { ...PLANTA_BASE }, recintos: {}, servicios: {} })

/** Cualquier plano guardado (v1 de 9a, v2, o nada) → versión 2 completa. No muta. */
export function normalizarPlano(plano) {
  const base = planoVacio()
  if (!plano || typeof plano !== 'object') return base
  if (plano.version === VERSION_PLANO) {
    return {
      ...base, ...plano,
      plantas: { ...base.plantas, ...(plano.plantas || {}) },
      recintos: { ...(plano.recintos || {}) },
      servicios: { ...(plano.servicios || {}) }
    }
  }
  // Versión 1: un bloque por motor con { poligono, cerrado, toma | rack }.
  for (const [clave, def] of Object.entries(RECINTO_DEL_MOTOR)) {
    const legado = plano[clave]
    if (!Array.isArray(legado?.poligono) || legado.poligono.length < 3) continue
    base.recintos[def.id] = {
      id: def.id, planta: 'pb', nombre: def.nombre, tipo: def.tipo,
      poligono: legado.poligono.map(aPunto), cerrado: !!legado.cerrado
    }
    const punto = aPunto(legado[def.campo])
    if (punto) base.servicios[def.servicio] = { planta: 'pb', ...punto }
  }
  return base
}

/** Lo que el lienzo de un motor espera hoy (`{ poligono, cerrado, toma|rack }`), o null. */
export function planoParaMotor(planoV2, clave) {
  const def = RECINTO_DEL_MOTOR[clave]
  if (!def) return null
  const recinto = planoV2?.recintos?.[def.id]
  if (!Array.isArray(recinto?.poligono) || recinto.poligono.length < 3) return null
  const servicio = planoV2.servicios?.[def.servicio]
  return {
    poligono: recinto.poligono.map(aPunto),
    cerrado: !!recinto.cerrado,
    [def.campo]: servicio ? { x: servicio.x, y: servicio.y } : null
  }
}

/* ---------- Vocabulario del recorrido (E1) ---------- */

/** Nombres que se ofrecen al cerrar un recinto. Tocar uno basta; escribir es la excepción. */
export const NOMBRES_RECINTO = [
  'Sala', 'Comedor', 'Cocina', 'Recámara', 'Recámara principal', 'Baño', 'Pasillo',
  'Estudio', 'Cuarto de lavado', 'Cochera', 'Jardín', 'Patio', 'Azotea'
]

/** Los contornos de la planta (F2): no se cotizan, acotan. El terreno es el predio de Cámaras. */
export const NOMBRES_CONTORNO = ['Terreno', 'Construcción']
export const CONTORNOS = ['predio', 'construccion']
export const esContorno = r => CONTORNOS.includes(r?.tipo)

/** El tipo sale del nombre; el vendedor no elige «exterior», elige «Jardín». */
export function tipoPorNombre(nombre) {
  const n = (nombre || '').toLowerCase()
  if (n.includes('predio') || n.includes('terreno')) return 'predio'
  if (n.includes('construcci')) return 'construccion'
  if (['jardín', 'jardin', 'patio', 'azotea', 'cochera', 'terraza'].some(t => n.includes(t))) return 'exterior'
  return 'interior'
}

/** Servicios fijos de la casa: se marcan una vez, los usan todos los rubros. */
export const SERVICIOS = [
  { id: 'tablero', nombre: 'Tablero', corto: 'T' },
  { id: 'medidorCFE', nombre: 'Medidor CFE', corto: 'M' },
  { id: 'tomaAgua', nombre: 'Toma de agua', corto: 'A' },
  { id: 'acometidaInternet', nombre: 'Llega el internet', corto: 'I' },
  { id: 'rack', nombre: 'Grabador / rack', corto: 'R' }
]

/** Id legible y único: «Recámara» → `recamara`, `recamara-2`, … */
export function idDeRecinto(nombre, existentes = {}) {
  const base = (nombre || 'recinto').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'recinto'
  if (!existentes[base]) return base
  let n = 2
  while (existentes[`${base}-${n}`]) n++
  return `${base}-${n}`
}

/**
 * Imán del trazo (plan §3): primero una esquina existente a menos de 0.35 m (la
 * esquina compartida entre dos recintos), luego la alineación ortogonal con la
 * esquina anterior a menos de 0.3 m, y si no, rejilla de 0.1 m (F2; era 0.5 y un baño
 * no cabía).
 */
export function imantar(p, esquinas = [], anterior = null) {
  for (const e of esquinas) if (Math.hypot(e.x - p.x, e.y - p.y) <= 0.35) return { x: e.x, y: e.y, iman: 'esquina' }
  let { x, y } = p
  let iman = null
  if (anterior) {
    if (Math.abs(x - anterior.x) <= 0.3) { x = anterior.x; iman = 'ortogonal' }
    if (Math.abs(y - anterior.y) <= 0.3) { y = anterior.y; iman = 'ortogonal' }
  }
  return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10, iman: iman || 'rejilla' }
}

/** Las habitaciones de una cotización nueva nacen de los recintos del plano (E1); los contornos no. */
export function habitacionesDesdePlano(planoV2) {
  const plantas = planoV2?.plantas || {}
  return Object.values(planoV2?.recintos || {}).filter(r => !esContorno(r))
    .sort((a, b) => ((plantas[a.planta]?.orden ?? 0) - (plantas[b.planta]?.orden ?? 0)) || (a.nombre || '').localeCompare(b.nombre || ''))
    .map(r => ({ id: r.id, nombre: r.nombre, recinto: r.id, planta: r.planta, items: [] }))
}

/** Orden de plantas para pestañas: planta baja, alta, … */
export const plantasOrdenadas = planoV2 =>
  Object.values(planoV2?.plantas || {}).sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))

/**
 * Parche (para `guardarParcial` sobre `casas`) con lo que el lienzo devolvió.
 * Escribe versión 2 y BORRA el bloque de versión 1 del mismo motor si lo hubiera:
 * a partir de la primera escritura, la única verdad es `recintos`/`servicios`.
 */
export function parcheDesdeMotor(clave, datos, ahora = new Date().toISOString()) {
  const def = RECINTO_DEL_MOTOR[clave]
  if (!def) throw new Error(`Motor sin recinto en el plano: ${clave}`)
  const punto = aPunto(datos?.[def.campo])
  return {
    plano: {
      version: VERSION_PLANO,
      plantas: { pb: PLANTA_BASE.pb },
      recintos: {
        [def.id]: {
          id: def.id, planta: 'pb', nombre: def.nombre, tipo: def.tipo,
          poligono: (datos?.poligono || []).map(aPunto), cerrado: !!datos?.cerrado,
          actualizadoEn: ahora
        }
      },
      servicios: { [def.servicio]: punto ? { planta: 'pb', x: punto.x, y: punto.y } : CENTINELA_BORRAR },
      [clave]: CENTINELA_BORRAR
    }
  }
}

/* ---------- Recintos rectangulares: la forma paramétrica (F1, 2026-08-22) ---------- */
/*
 * `forma = { tipo:'rect', cx, cy, ancho, largo, ang }` en metros y grados (sentido
 * horario, porque en el SVG la y crece hacia abajo). El polígono SIGUE siendo la verdad
 * que consumen trayectos, cobertura, Riego y Cámaras: toda edición de la forma reescribe
 * `poligono` con `poligonoDeForma`. Un recinto trazado a mano no tiene `forma` y se
 * edita por esquinas, como antes.
 */

export const LADOS = ['arriba', 'derecha', 'abajo', 'izquierda']
const rad = g => g * Math.PI / 180
const cm = v => Math.round(v * 100) / 100
export const normalizarAngulo = a => ((a % 360) + 360) % 360

/** Las cuatro esquinas (arriba-izq, arriba-der, abajo-der, abajo-izq) de la forma. */
export function poligonoDeForma(f) {
  const c = Math.cos(rad(f.ang || 0)), s = Math.sin(rad(f.ang || 0))
  const w = f.ancho / 2, l = f.largo / 2
  return [[-w, -l], [w, -l], [w, l], [-w, l]].map(([x, y]) => ({
    x: cm(f.cx + x * c - y * s), y: cm(f.cy + x * s + y * c)
  }))
}

/** Normal hacia afuera de un lado, ya rotada. Lado 0 arriba, 1 derecha, 2 abajo, 3 izquierda. */
export function normalDeLado(f, lado) {
  const n = [[0, -1], [1, 0], [0, 1], [-1, 0]][lado]
  const c = Math.cos(rad(f.ang || 0)), s = Math.sin(rad(f.ang || 0))
  return { x: n[0] * c - n[1] * s, y: n[0] * s + n[1] * c }
}

/** Punto medio de un lado (donde va la manija). */
export function medioDeLado(f, lado) {
  const n = normalDeLado(f, lado)
  const d = (lado % 2 === 0 ? f.largo : f.ancho) / 2
  return { x: cm(f.cx + n.x * d), y: cm(f.cy + n.y * d) }
}

/** Un punto del mundo en coordenadas locales de la forma (sin rotación, centro en 0). */
export function aLocal(f, p) {
  const c = Math.cos(rad(-(f.ang || 0))), s = Math.sin(rad(-(f.ang || 0)))
  const x = p.x - f.cx, y = p.y - f.cy
  return { x: x * c - y * s, y: x * s + y * c }
}

/**
 * Estira UN lado hasta la medida nueva dejando fijo el lado opuesto (la regla de ancla
 * del inspector: «la pared pegada se queda, crece la opuesta»). Mínimo 0.5 m.
 */
export function redimensionarLado(f, lado, medida) {
  const m = Math.max(0.5, cm(medida))
  const esLargo = lado % 2 === 0
  const vieja = esLargo ? f.largo : f.ancho
  const n = normalDeLado(f, lado)
  const d = (m - vieja) / 2
  return { ...f, [esLargo ? 'largo' : 'ancho']: m, cx: cm(f.cx + n.x * d), cy: cm(f.cy + n.y * d) }
}

/** Cambia una medida dejando fijo el CENTRO (cuando ninguna pared está pegada). */
export function redimensionarCentrado(f, campo, medida) {
  return { ...f, [campo]: Math.max(0.5, cm(medida)) }
}

/** La medida que implica arrastrar la manija del lado `lado` hasta el punto `p`. */
export function medidaDesdePunto(f, lado, p) {
  const q = aLocal(f, p)
  const esLargo = lado % 2 === 0
  const signo = (lado === 0 || lado === 3) ? -1 : 1
  const desdeOpuesto = signo * (esLargo ? q.y : q.x) + (esLargo ? f.largo : f.ancho) / 2
  return Math.max(0.5, Math.round(desdeOpuesto * 10) / 10)
}

/** Ángulo de la manija de rotación: el punto `p` visto desde el centro, con «arriba» = 0°. */
export function anguloDesdePunto(f, p) {
  return normalizarAngulo(Math.atan2(p.y - f.cy, p.x - f.cx) * 180 / Math.PI + 90)
}

/**
 * Imán de ángulo: múltiplos de 15° a ≤ 4°, y los de 45° (0, 45, 90 …) a ≤ 8° — pegan más
 * fuerte porque son los que casi siempre son ciertos. `libre` lo desactiva (segundo dedo
 * en el lienzo o Shift).
 */
export function imanAngulo(a, libre = false) {
  const ang = normalizarAngulo(a)
  if (libre) return { ang: Math.round(ang * 10) / 10, iman: null }
  const a45 = Math.round(ang / 45) * 45
  if (Math.abs(ang - a45) <= 8) return { ang: normalizarAngulo(a45), iman: '45' }
  const a15 = Math.round(ang / 15) * 15
  if (Math.abs(ang - a15) <= 4) return { ang: normalizarAngulo(a15), iman: '15' }
  return { ang: Math.round(ang * 10) / 10, iman: null }
}

/** Distancia de un punto a un segmento. */
export function distanciaASegmento(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y
  const l2 = dx * dx + dy * dy
  const t = l2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

/**
 * Qué lado de la forma está pegado a otro recinto (≤ `tolerancia` m de alguna arista
 * ajena): ese es el que se queda fijo al teclear una medida. Devuelve el índice del lado
 * más pegado, o null si ninguno. Si dos lados opuestos están pegados, gana el más cercano.
 */
export function ladoPegado(f, otros = [], tolerancia = 0.25) {
  let mejor = null
  for (let lado = 0; lado < 4; lado++) {
    const m = medioDeLado(f, lado)
    for (const r of otros) {
      const pts = r.poligono || []
      for (let i = 0; i < pts.length; i++) {
        const d = distanciaASegmento(m, pts[i], pts[(i + 1) % pts.length])
        if (d <= tolerancia && (!mejor || d < mejor.d)) mejor = { lado, d }
      }
    }
  }
  return mejor ? mejor.lado : null
}

/**
 * Teclear una medida en el inspector: si hay un lado pegado en ese eje, se queda ese y crece
 * el opuesto; si no, crece desde el centro.
 */
export function aplicarMedida(f, campo, medida, otros = []) {
  const pegado = ladoPegado(f, otros)
  const ejeDelPegado = pegado === null ? null : (pegado % 2 === 0 ? 'largo' : 'ancho')
  if (ejeDelPegado === campo) return redimensionarLado(f, (pegado + 2) % 4, medida)
  return redimensionarCentrado(f, campo, medida)
}

/**
 * «Cuadrar»: un trazo de 4 esquinas casi rectangular (ángulos a ±7° de 90°) se convierte
 * en forma. Devuelve null si no es un rectángulo razonable.
 */
export function formaDesdePoligono(pts) {
  if (!Array.isArray(pts) || pts.length !== 4) return null
  for (let i = 0; i < 4; i++) {
    const a = pts[(i + 3) % 4], b = pts[i], c = pts[(i + 1) % 4]
    const u = { x: a.x - b.x, y: a.y - b.y }, v = { x: c.x - b.x, y: c.y - b.y }
    const cosT = (u.x * v.x + u.y * v.y) / ((Math.hypot(u.x, u.y) * Math.hypot(v.x, v.y)) || 1)
    if (Math.abs(Math.acos(Math.max(-1, Math.min(1, cosT))) * 180 / Math.PI - 90) > 7) return null
  }
  const ang = normalizarAngulo(Math.atan2(pts[1].y - pts[0].y, pts[1].x - pts[0].x) * 180 / Math.PI)
  const ancho = (Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y) + Math.hypot(pts[2].x - pts[3].x, pts[2].y - pts[3].y)) / 2
  const largo = (Math.hypot(pts[2].x - pts[1].x, pts[2].y - pts[1].y) + Math.hypot(pts[3].x - pts[0].x, pts[3].y - pts[0].y)) / 2
  const cx = pts.reduce((s, p) => s + p.x, 0) / 4, cy = pts.reduce((s, p) => s + p.y, 0) / 4
  return { tipo: 'rect', cx: cm(cx), cy: cm(cy), ancho: cm(ancho), largo: cm(largo), ang: Math.round(ang * 10) / 10 }
}

/** Un recinto rectangular nuevo, listo para `recintos[id]`. */
export function recintoRectangular({ id, planta, nombre, cx, cy, ancho = 3, largo = 3, ang = 0, ahora = new Date().toISOString() }) {
  const forma = { tipo: 'rect', cx: cm(cx), cy: cm(cy), ancho, largo, ang }
  return { id, planta, nombre, tipo: tipoPorNombre(nombre), forma, poligono: poligonoDeForma(forma), cerrado: true, actualizadoEn: ahora }
}

/* ---------- F2: el imán con muro, los contornos y el solape (2026-08-23) ---------- */
/*
 * Lo que hace que «pegar una recámara a la sala» deje el muro de por medio. Prioridad
 * (propuesta §3.4): pared con pared dejando el muro → esquinas alineadas a lo largo de
 * esa pared → dentro del contorno sin muro → rejilla. El radio llega en metros pero
 * nace en píxeles de pantalla (10 px ÷ escala), como en tldraw.
 *
 * Grosor de muro por defecto: 0.15 m interior, 0.20 m cuando uno de los dos es exterior.
 * `sin verificar` contra obra mexicana (tabique de 14 cm aplanado ≈ 0.15); CubiCasa usa
 * 0.10 / 0.15. Editable en `plano.preferencias`.
 */

export const PREFERENCIAS_PLANO = Object.freeze({ muroInterior: 0.15, muroExterior: 0.20, rejilla: 0.1 })

const dirDe = (a, b) => { const dx = b.x - a.x, dy = b.y - a.y, n = Math.hypot(dx, dy) || 1; return { x: dx / n, y: dy / n } }
const proy = (p, d) => p.x * d.x + p.y * d.y
const SENO_3 = Math.sin(3 * Math.PI / 180)
const aristasDe = pts => pts.map((a, i) => ({ a, b: pts[(i + 1) % pts.length] }))

/** El muro que debe quedar entre dos recintos. Con un contorno no hay muro: la pared es el contorno. */
export function muroEntre(r, s, pref = PREFERENCIAS_PLANO) {
  if (esContorno(r) || esContorno(s)) return 0
  if (r?.tipo === 'exterior' || s?.tipo === 'exterior') return pref.muroExterior
  return pref.muroInterior
}

/**
 * Candidatos de pared con pared para el lado `lado` de la forma `f`: aristas ajenas
 * paralelas (± 3°) que se traslapan al menos 0.2 m a lo largo del lado, con `delta` = lo
 * que habría que mover ese lado hacia afuera para dejar exactamente el muro.
 */
function candidatosDeLado(f, lado, recinto, otros, radio, pref) {
  const pts = poligonoDeForma(f)
  const a = pts[lado], b = pts[(lado + 1) % 4]
  const n = normalDeLado(f, lado), t = dirDe(a, b), m = medioDeLado(f, lado)
  const out = []
  for (const s of otros) {
    if (!s?.poligono || s.poligono.length < 2) continue
    const muro = muroEntre(recinto, s, pref)
    for (const { a: sa, b: sb } of aristasDe(s.poligono)) {
      const ts = dirDe(sa, sb)
      if (Math.abs(t.x * ts.y - t.y * ts.x) > SENO_3) continue
      const pr = [proy(a, t), proy(b, t)], ps = [proy(sa, t), proy(sb, t)]
      const lo = Math.max(Math.min(...pr), Math.min(...ps)), hi = Math.min(Math.max(...pr), Math.max(...ps))
      if (hi - lo < 0.2) continue
      const d = proy({ x: sa.x - m.x, y: sa.y - m.y }, n)      // dónde está la pared ajena, hacia afuera
      const delta = d - muro
      if (Math.abs(delta) <= radio) out.push({ lado, n, t, delta, muro, s, sa, sb, lo, hi, d })
    }
  }
  return out.sort((p, q) => Math.abs(p.delta) - Math.abs(q.delta))
}

/** La banda del muro entre el lado `lado` de `f` y la pared ajena, para dibujarla. */
function bandaDe(f, c) {
  const pts = poligonoDeForma(f)
  const a = pts[c.lado], t = c.t, n = c.n
  const base = { x: a.x + t.x * (c.lo - proy(a, t)), y: a.y + t.y * (c.lo - proy(a, t)) }
  const largo = c.hi - c.lo
  const p1 = base, p2 = { x: base.x + t.x * largo, y: base.y + t.y * largo }
  const p3 = { x: p2.x + n.x * c.muro, y: p2.y + n.y * c.muro }, p4 = { x: p1.x + n.x * c.muro, y: p1.y + n.y * c.muro }
  return { tipo: c.muro > 0 ? 'muro' : 'contorno', puntos: [p1, p2, p3, p4], muro: c.muro, n }
}

/**
 * Imán al MOVER una forma: devuelve la forma ya pegada y las guías para dibujar.
 * `otros` son los recintos visibles de la planta (sin el que se mueve).
 */
export function imanDeForma(forma, recinto, otros = [], radio = 0.2, pref = PREFERENCIAS_PLANO) {
  let f = { ...forma }
  const guias = []
  let primero = null
  // 1) pared con pared, a lo más una por eje
  const todos = [0, 1, 2, 3].flatMap(l => candidatosDeLado(f, l, recinto, otros, radio, pref))
    .sort((p, q) => Math.abs(p.delta) - Math.abs(q.delta))
  for (const c of todos) {
    if (primero && Math.abs(proy(primero.n, c.n)) > 0.5) continue     // mismo eje: ya pegó
    f = { ...f, cx: cm(f.cx + c.n.x * c.delta), cy: cm(f.cy + c.n.y * c.delta) }
    guias.push(bandaDe(f, { ...c, delta: 0 }))
    if (!primero) primero = c
    else break
  }
  // 2) esquinas alineadas a lo largo de la pared pegada
  if (primero) {
    const pts = poligonoDeForma(f)
    const mios = [pts[primero.lado], pts[(primero.lado + 1) % 4]]
    let mejor = null
    for (const mio of mios) for (const suyo of [primero.sa, primero.sb]) {
      const diff = proy({ x: suyo.x - mio.x, y: suyo.y - mio.y }, primero.t)
      if (Math.abs(diff) <= radio && (!mejor || Math.abs(diff) < Math.abs(mejor.diff))) mejor = { diff, suyo }
    }
    if (mejor) {
      f = { ...f, cx: cm(f.cx + primero.t.x * mejor.diff), cy: cm(f.cy + primero.t.y * mejor.diff) }
      guias.push({ tipo: 'esquina', p: mejor.suyo, n: primero.n })
    }
  }
  // 3) si nada pegó, rejilla
  if (!primero) {
    const g = pref.rejilla || 0.1
    f = { ...f, cx: Math.round(f.cx / g) * g, cy: Math.round(f.cy / g) * g }
    f.cx = cm(f.cx); f.cy = cm(f.cy)
  }
  return { forma: f, guias, iman: primero ? (primero.muro > 0 ? 'muro' : 'contorno') : null }
}

/**
 * Imán al ESTIRAR un lado: ajusta la medida para que la pared nueva quede a un muro de la
 * ajena. Una medida con candado no se toca.
 */
export function imanDeLado(forma, lado, medida, recinto, otros = [], radio = 0.2, pref = PREFERENCIAS_PLANO, candado = false) {
  if (candado) return { medida, guias: [] }
  const f = redimensionarLado(forma, lado, medida)
  const c = candidatosDeLado(f, lado, recinto, otros, radio, pref)[0]
  if (!c) return { medida, guias: [] }
  const ajustada = Math.max(0.5, cm(medida + c.delta))
  const f2 = redimensionarLado(forma, lado, ajustada)
  return { medida: ajustada, guias: [bandaDe(f2, { ...c, delta: 0 })] }
}

/* ---------- Solape ---------- */

const orient = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x)
function segmentosSeCruzan(a, b, c, d) {
  const o1 = orient(a, b, c), o2 = orient(a, b, d), o3 = orient(c, d, a), o4 = orient(c, d, b)
  return o1 * o2 < -1e-9 && o3 * o4 < -1e-9           // cruce propio; tocarse no cuenta
}

/** Dos polígonos se solapan si un vértice cae dentro del otro o dos aristas se cruzan. */
export function poligonosSeSolapan(A = [], B = []) {
  if (A.length < 3 || B.length < 3) return false
  const dentroDe = (p, pts) => {
    let d = false
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      const yi = pts[i].y, yj = pts[j].y, xi = pts[i].x, xj = pts[j].x
      if ((yi > p.y) !== (yj > p.y) && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi) d = !d
    }
    return d
  }
  const encoge = pts => {                                   // 1 cm hacia adentro: tocarse no es solapar
    const c = pts.reduce((s, p) => ({ x: s.x + p.x / pts.length, y: s.y + p.y / pts.length }), { x: 0, y: 0 })
    return pts.map(p => ({ x: p.x + (c.x - p.x) * 0.01, y: p.y + (c.y - p.y) * 0.01 }))
  }
  const a = encoge(A), b = encoge(B)
  if (a.some(p => dentroDe(p, b)) || b.some(p => dentroDe(p, a))) return true
  for (const e of aristasDe(a)) for (const g of aristasDe(b)) if (segmentosSeCruzan(e.a, e.b, g.a, g.b)) return true
  return false
}

/** Ids de los recintos (no contornos) que se solapan con alguno de los demás. */
export function recintosSolapados(recintos = []) {
  const ids = new Set()
  const lista = recintos.filter(r => !esContorno(r) && r.poligono?.length >= 3)
  for (let i = 0; i < lista.length; i++) for (let j = i + 1; j < lista.length; j++) {
    if (poligonosSeSolapan(lista[i].poligono, lista[j].poligono)) { ids.add(lista[i].id); ids.add(lista[j].id) }
  }
  return ids
}
